import React, { useEffect } from "react";
import { Container, Tab, Tabs, Spinner, Alert } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  onChangeSettingsActiveTab,
  fetchStatus,
} from "../store/slices/preferenceSlice";
import {
  clearDefaultValues,
  fetchShopifyFields,
  fetchMsbcFields,
} from "../store/slices/mappingSlice";
import { fetchMsbcDefaultFields } from "../store/slices/contactSlice";
import { fetchStages } from "../store/slices/invoiceSlice";

import ContactSync from "./ContactSync";
import InvoiceSync from "./InvoiceSync";
import ProductSync from "./ProductSync";

// CMS Helpers
import { useCmsHook } from "../helpers/cmsHook";
import { CmsConstructor } from "../helpers/cmsConstructor";
import CmsEditAndSave from "../helpers/CmsEditAndSave";

const SyncSettings = () => {
  const tabKeys = Object.freeze({
    contact: "contact",
    invoice: "invoice",
    product: "product",
  });

  const { cmsInstance, setCmsInstance } = useCmsHook();

  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  // preference states
  const { settingsActiveTab, isMsbcConnected, tenantId } = useSelector(
    (state) => state.preference
  );

  // contact states
  const { isContactsSyncRulesSaved, isContactDefaultMappingsSaved } =
    useSelector((state) => state.contact);

  // invoice states
  const { isInvoiceSyncRulesSaved } = useSelector((state) => state.invoice);

  // product states
  const { isProductSyncRulesSaved } = useSelector((state) => state.product);

  // mapping states
  const {
    isMappingsSaved: {
      customer: isCustomerMappingsSaved,
      invoice: isInvoiceMappingsSaved,
      productFields: isProductMappingsSaved,
    },
    shopify: { loading: shopifyLoading },
    msbc: { loading: msbcLoading },
  } = useSelector((state) => state.mapping);

  useEffect(() => {
    window.scrollTo(0, 0);
    const shop = searchParams.get("shop");
    if (shop) {
      dispatch(fetchStatus({ shop }));
      dispatch(fetchShopifyFields({ shop }));
      dispatch(fetchStages({ shop }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shop = searchParams.get("shop");

    if (isMsbcConnected && shop) {
      dispatch(fetchMsbcFields({ shop }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMsbcConnected]);

  useEffect(() => {
    const shop = searchParams.get("shop");

    if (shop && tenantId && isMsbcConnected) {
      dispatch(fetchMsbcDefaultFields({ shop, tenantId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMsbcConnected]);

  useEffect(() => {
    if (window.location.pathname === "/syncSettings") {
      const cmsInstance = async () => {
        const cmsObj = await CmsConstructor();
        setCmsInstance(cmsObj);
      };
      cmsInstance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-container">
      {!cmsInstance ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <div className="d-flex flex-row justify-content-center align-items-center">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </div>
      ) : (
        <>
          {window.location.pathname === "/settings" && (
            <div>
              <CmsEditAndSave />
            </div>
          )}
          {!isMsbcConnected && (!tenantId || tenantId === "") && (
            <div className="d-flex flex-row align-items-center justify-content-center pt-4">
              <Alert variant="warning" className="w-50 mb-0 alert-message">
                Please connect your Microsoft Business Central account.
              </Alert>
            </div>
          )}
          <Container className="py-5">
            <div className="sync-settings-tab-container py-3 px-4">
              <Tabs
                activeKey={settingsActiveTab}
                onSelect={(selectedKey) => {
                  dispatch(clearDefaultValues());
                  dispatch(
                    onChangeSettingsActiveTab({
                      selectedTab: selectedKey,
                      isContactsSyncRulesSaved,
                      isContactDefaultMappingsSaved,
                      isCustomerMappingsSaved,
                      isInvoiceSyncRulesSaved,
                      isInvoiceMappingsSaved,
                      isProductSyncRulesSaved,
                      isProductMappingsSaved,
                      shopifyLoading,
                      msbcLoading,
                    })
                  );
                }}
                className="sync-settings-tabs"
              >
                <Tab eventKey={tabKeys.contact} title="Customer">
                  <ContactSync />
                </Tab>
                <Tab eventKey={tabKeys.product} title="Products">
                  <ProductSync />
                </Tab>
                <Tab eventKey={tabKeys.invoice} title="Invoices">
                  <InvoiceSync />
                </Tab>
              </Tabs>
            </div>
          </Container>
        </>
      )}
    </div>
  );
};

export default SyncSettings;
